import { CommonModule } from '@angular/common';
import { NgModule, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { AnalyticsService, TrackLinkDirective, TrackSeenDirective, ConfigService } from 'wdpr-angular-analytics';

/* eslint-disable max-len */
import { AagComponent } from '@finder/features/detail/components/aag/aag.component';
import { AagDurationComponent } from '@finder/features/detail/components/aag-duration/aag-duration.component';
import { AagFacetModule } from '@finder/shared/components/aag-facet/aag-facet.module';
import { AagListItemComponent } from '@finder/features/detail/components/aag-list-item/aag-list-item.component';
import { AagStartingFromPriceComponent } from '@finder/features/detail/components/aag-starting-from-price/aag-starting-from-price.component';
import { AccessibilityModalModule } from '@finder/shared/components/a11y-modal/a11y-modal.module';
import { AnchorModule } from '@finder/shared/components/anchor/anchor.module';
import { AriaHiddenModule } from '@finder/shared/directives/aria-hidden/aria-hidden.module';
import { AvailabilityCalendarModule } from '@finder/shared/components/availability-calendar/availability-calendar.module';
import { AvailabilityModalComponent } from '@finder/shared/components/availability-modal/availability-modal.component';
import { AvatarCheckboxComponent } from '@finder/shared/components/avatar-checkbox/avatar-checkbox.component';
import { AvatarListModule } from '@finder/shared/components/avatar-list/avatar-list.module';
import { AvatarModule } from '@finder/shared/components/avatar/avatar.module';
import { BlockLayoutRendererModule } from '@finder/shared/components/block-layout-renderer/block-layout-renderer.module';
import { BreadcrumbModule } from '@finder/shared/components/breadcrumb/breadcrumb.module';
import { ButtonCardModule } from '@finder/shared/components/button-card/button-card.module';
import { ComponentHeaderModule } from '@finder/shared/components/component-header/component-header.module';
import { ComponentsModule } from '@finder/shared/components/components.module';
import { ContentHeaderModule } from '@finder/shared/components/content-header/content-header.module';
import { ContentNavigationModule } from '@finder/shared/components/content-navigation/content-navigation.module';
import { CountdownClockModule } from '@finder/shared/components/countdown-clock/countdown-clock.module';
import { CurrencyConverterModule } from '@finder/shared/components/currency-converter/currency-converter.module';
import { DatePickerWrapperModule } from '@finder/shared/components/date-picker-wrapper/date-picker-wrapper.module';
import { DescriptiveTextModule } from '@finder/shared/components/descriptive-text/descriptive-text.module';
import { DetailComponent } from '@finder/features/detail/detail.component';
import { DetailRoutingModule } from '@finder/features/detail/detail-routing.module';
import { DfmModule } from '@finder/shared/components/dfm/dfm.module';
import { ElementChangeModule } from '@finder/shared/directives/element-change/element-change.module';
import { FeaturedMenuItemsComponent } from '@finder/features/detail/components/featured-menu-items/featured-menu-items.component';
import { FinderInViewportModule } from '@finder/shared/directives/in-viewport/in-viewport.module';
import { FinderTransitionInModule } from '@finder/shared/directives/transition-in/transition-in.module';
import { FontIconModule } from '@finder/shared/components/font-icon/font-icon.module';
import { FormatTimePipeModule } from '@finder/shared/utils/pipes/format-time/format-time.module';
import { GenericTextModule } from '@finder/shared/components/generic-text/generic-text.module';
import { GuestsAlsoViewedComponent } from '@finder/features/detail/components/guests-also-viewed/guests-also-viewed.component';
import { GuestServiceMapModule } from '@finder/shared/components/guest-service-map/guest-service-map.module';
import { HeroMediaModule } from '@finder/shared/components/hero-media/hero-media.module';
import { HorizontalRuleModule } from '@finder/shared/components/horizontal-rule/horizontal-rule.module';
import { IconTooltipModule } from '@finder/shared/components/icon-tooltip/icon-tooltip.module';
import { IframeModule } from '@finder/shared/components/iframe/iframe.module';
import { ImportantMessageModule } from '@finder/shared/components/important-message/important-message.module';
import { InputModule } from '@finder/shared/components/input/input.module';
import { InterstitialTriggerModule } from '@finder/shared/directives/interstitial-trigger/interstitial-trigger.module';
import { LearnMoreComponent } from '@finder/shared/components/learn-more/learn-more.component';
import { LocaleDirectiveModule } from '@finder/shared/directives/locale/locale.module';
import { MapsComponent } from '@finder/features/detail/components/maps/maps.component';
import { MealPeriodModule } from '@finder/shared/components/meal-period/meal-period.module';
import { MealPeriodScheduleService } from '@finder/shared/services/meal-period-schedule/meal-period-schedule.service';
import { MediaGalleryModule } from '@finder/shared/components/media-gallery/media-gallery.module';
import { MerchandiseGroupModule } from './components/merchandise-group/merchandise-group.module';
import { ModalModule } from '@finder/shared/components/modal/modal.module';
import { MoreLikeThisComponent } from '@finder/features/detail/components/more-like-this/more-like-this.component';
import { MyPlansModalComponent } from '@finder/shared/components/my-plans-modal/my-plans-modal.component';
import { MyPlansService } from '@finder/shared/services/my-plans-service/my-plans.service';
import { OperatingStatusComponent } from '@finder/shared/components/operating-status/operating-status.component';
import { PackageCardModule } from '@finder/shared/components/package-card/package-card.module';
import { PageMessageModule } from '@finder/shared/components/page-message/page-message.module';
import { ParticipatingRestaurantsComponent } from '@finder/features/detail/components/participating-restaurants/participating-restaurants.component';
import { PictureModule } from '@finder/shared/components/picture/picture.module';
import { PricingGridModule } from '@finder/shared/components/pricing-grid/pricing-grid.module';
import { PromoBannerModule } from '@finder/shared/components/promo-banner/promo-banner.module';
import { PromoGroupComponent } from '@finder/features/detail/components/promo-group/promo-group.component';
import { PromoModule } from '@finder/shared/components/promo/promo.module';
import { ReadMoreModalComponent } from '@finder/shared/components/read-more-modal/read-more-modal.component';
import { RelatedActivitiesComponent } from '@finder/features/detail/components/related-activities/related-activities.component';
import { RevealContentModule } from '@finder/shared/components/reveal-content/reveal-content.module';
import { RouterLinkModule } from '@finder/shared/directives/router-link/router-link.module';
import { SafeHtmlPipeModule } from '@finder/shared/utils/pipes/safe-html/safe.html.module';
import { SamplePackagesGroupModule } from '@finder/shared/components/sample-packages/sample-packages.module';
import { ScheduleModule } from '@finder/shared/components/schedule/schedule.module';
import { ScheduleService } from '@finder/shared/services/schedule/schedule.service';
import { ScrollToModule } from '@finder/shared/directives/scroll-to/scroll-to.module';
import { SeatingChartComponent } from '@finder/features/detail/components/seating-chart/seating-chart.component';
import { SessionStorage } from '@finder/shared/services/session-storage/session-storage.service';
import { ShowtimesModalModule } from '@finder/shared/components/showtimes-modal/showtimes-modal.module';
import { StepBlockModule } from '@finder/shared/components/step-block/step-block.module';
import { StorycardModule } from '@finder/shared/components/storycard/storycard.module';
import { TableModule } from '@finder/shared/components/table/table.module';
import { TextComponent } from '@finder/features/detail/components/text/text.component';
import { TimePeriodPipe } from '@finder/shared/utils/pipes/period/time-period.pipe';
import { TitleModule } from '@finder/shared/components/title/title.module';
import { TripAdvisorComponent } from '@finder/features/detail/components/trip-advisor/trip-advisor.component';
import { TutorialModule } from '@finder/shared/components/tutorial/tutorial.module';
import { VirtualQueueModalModule } from '@finder/shared/components/virtual-queue-modal/virtual-queue-modal.module';
import { VisualItineraryModule } from '@finder/shared/components/visual-itinerary/visual-itinerary.module';
import { WebSocketsService } from '@finder/shared/services/websockets/websockets.service';
import { OperatingStatusService } from '@finder/shared/components/operating-status/operating-status.service';

/* eslint-enable max-len */

const exportableComponents = [
    AagComponent,
    AagDurationComponent,
    AagListItemComponent,
    AagStartingFromPriceComponent,
    AvailabilityModalComponent,
    AvatarCheckboxComponent,
    DetailComponent,
    FeaturedMenuItemsComponent,
    GuestsAlsoViewedComponent,
    LearnMoreComponent,
    MapsComponent,
    MoreLikeThisComponent,
    MyPlansModalComponent,
    ParticipatingRestaurantsComponent,
    PromoGroupComponent,
    ReadMoreModalComponent,
    RelatedActivitiesComponent,
    SeatingChartComponent,
    TextComponent,
    TimePeriodPipe,
    TripAdvisorComponent,
];

const exportableModules = [
    AagFacetModule,
    AccessibilityModalModule,
    AnchorModule,
    AriaHiddenModule,
    AvailabilityCalendarModule,
    BreadcrumbModule,
    ButtonCardModule,
    ComponentHeaderModule,
    ContentNavigationModule,
    CountdownClockModule,
    CurrencyConverterModule,
    DatePickerWrapperModule,
    DescriptiveTextModule,
    ElementChangeModule,
    FinderInViewportModule,
    FinderTransitionInModule,
    FontIconModule,
    FormatTimePipeModule,
    GenericTextModule,
    GuestServiceMapModule,
    HeroMediaModule,
    HorizontalRuleModule,
    IconTooltipModule,
    IframeModule,
    ImportantMessageModule,
    InputModule,
    InterstitialTriggerModule,
    LocaleDirectiveModule,
    MealPeriodModule,
    MerchandiseGroupModule,
    ModalModule,
    OperatingStatusComponent,
    PackageCardModule,
    PictureModule,
    PricingGridModule,
    PromoBannerModule,
    PromoModule,
    RevealContentModule,
    RouterLinkModule,
    SafeHtmlPipeModule,
    SamplePackagesGroupModule,
    ScheduleModule,
    ScrollToModule,
    ShowtimesModalModule,
    StepBlockModule,
    StorycardModule,
    TableModule,
    TitleModule,
    TrackLinkDirective,
    TrackSeenDirective,
    TutorialModule,
    VirtualQueueModalModule,
    VisualItineraryModule,
];

@NgModule({
    imports: [
        CommonModule,
        ComponentsModule,
        ContentHeaderModule,
        DetailRoutingModule,
        AvatarModule,
        AvatarListModule,
        DfmModule,
        PageMessageModule,
        BlockLayoutRendererModule,
        MediaGalleryModule,
        ...exportableModules,
    ],
    declarations: [
        exportableComponents
    ],
    providers: [
        ConfigService,
        AnalyticsService,
        ScheduleService,
        SessionStorage,
        MealPeriodScheduleService,
        MyPlansService,
        OperatingStatusService,
        WebSocketsService,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    exports: [
        ...exportableComponents,
        ...exportableModules,
    ]
})
export class DetailModule {
    static entry = DetailComponent;
}
